import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  useAuth,
  useTenantBootstrap,
  useTenantFamily,
  useTenantFamilyRegistrationCheckout,
  useTenantFamilySchedule,
} from '@tark/shared/hooks';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input } from '@tark/shared/components';
import { cn } from '@tark/shared/lib';
import type {
  TenantFamilyMakeupCreditSummary,
  TenantFamilyAthleteBalanceSummary,
  TenantFamilyAthleteDevelopmentSummary,
  TenantFamilyReceiptSummary,
  TenantFamilyScheduleAthleteBooking,
  TenantFamilyScheduleSession,
} from '@tark/shared/types';
import {
  CalendarDays,
  CheckCircle2,
  Clock3,
  CreditCard,
  ExternalLink,
  Flag,
  HeartHandshake,
  Loader2,
  MapPin,
  Plus,
  RefreshCcw,
  ReceiptText,
  Users2,
} from 'lucide-react';
import { FamilyNotesPanel } from '../components/family/FamilyNotesPanel';
import { CARD_CHECKOUT_ENABLED, PAY_OUTSIDE_COPY } from '../lib/cardCheckoutPolicy';
import { FamilyBookableOpeningsPanel } from '../components/family/FamilyBookableOpeningsPanel';
import { FamilyMatchPlayPanel } from '../components/family/FamilyMatchPlayPanel';

type BookingAction = 'REQUEST' | 'WAITLIST' | 'TRIAL';

export default function FamilyPage() {
  const { user } = useAuth();
  const { activeTenant } = useTenantBootstrap(Boolean(user));
  const canUseFamilyFlow = Boolean(activeTenant?.tenant?.id);
  const { household, isLoading, isError, refetch, createAthlete, isCreatingAthlete } = useTenantFamily(
    Boolean(user) && canUseFamilyFlow
  );
  const {
    schedule,
    isLoading: isScheduleLoading,
    isError: isScheduleError,
    refetch: refetchSchedule,
    requestBooking,
  } = useTenantFamilySchedule(Boolean(user) && canUseFamilyFlow);
  const registrationCheckout = useTenantFamilyRegistrationCheckout();
  const [formState, setFormState] = useState({
    givenName: '',
    familyName: '',
    preferredName: '',
    birthDate: '',
    genderCode: '',
    sportCode: '',
    levelCode: '',
    relationshipCode: 'PARENT',
  });
  const [error, setError] = useState<string | null>(null);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [pendingRequestKey, setPendingRequestKey] = useState<string | null>(null);
  const [selectedAthleteId, setSelectedAthleteId] = useState<number | null>(null);

  const initialSportCode = useMemo(() => activeTenant?.tenant?.sportFamily || '', [activeTenant?.tenant?.sportFamily]);
  const tenantName = household?.activeTenant?.displayName || activeTenant?.tenant?.displayName || 'Organization';
  const stats = schedule?.summary;
  const makeupStats = schedule?.makeupSummary;
  const balanceSummaries = useMemo(
    () => (schedule?.billingSummaries ?? []).filter((item) =>
      (item.creditsRemaining ?? 0) > 0 ||
      item.activePackageCount > 0 ||
      item.unpaidBookingCount > 0 ||
      (item.packages ?? []).length > 0
    ),
    [schedule?.billingSummaries]
  );
  const receiptSummaries = useMemo(
    () => (schedule?.receiptSummaries ?? []).filter((item) =>
      Boolean(item.registrationId) &&
      (Boolean(item.receiptNumber) ||
        Boolean(item.invoiceNumber) ||
        Boolean(item.receiptUrl) ||
        Boolean(item.receiptShareToken) ||
        Boolean(item.externalPaymentUrl) ||
        (item.balanceAmount ?? 0) > 0 ||
        Boolean(item.paymentStatus))
    ),
    [schedule?.receiptSummaries]
  );
  const developmentSummaries = useMemo(
    () => (schedule?.developmentSummaries ?? []).filter((item) =>
      item.openAssignmentCount > 0 ||
      item.completedAssignmentCount > 0 ||
      item.activeGoalCount > 0 ||
      item.achievedGoalCount > 0 ||
      (item.goals ?? []).length > 0 ||
      (item.assignments ?? []).length > 0
    ),
    [schedule?.developmentSummaries]
  );
  useEffect(() => {
    if (!household?.athletes.length) {
      setSelectedAthleteId(null);
      return;
    }
    if (!household.athletes.some((athlete) => athlete.athleteId === selectedAthleteId)) {
      setSelectedAthleteId(household.athletes[0].athleteId);
    }
  }, [household?.athletes, selectedAthleteId]);
  const selectedAthlete = household?.athletes.find((athlete) => athlete.athleteId === selectedAthleteId) ?? null;
  const openMakeupCreditsByAthleteId = useMemo(() => {
    const map = new Map<number, TenantFamilyMakeupCreditSummary[]>();
    (schedule?.makeupCredits ?? [])
      .filter((credit) => credit.canRequest && (credit.creditStatus || '').toUpperCase() === 'OPEN')
      .forEach((credit) => {
        const list = map.get(credit.athleteId) ?? [];
        list.push(credit);
        map.set(credit.athleteId, list);
      });
    return map;
  }, [schedule?.makeupCredits]);

  const handleCreateAthlete = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    try {
      await createAthlete({
        givenName: formState.givenName,
        familyName: formState.familyName,
        preferredName: formState.preferredName || undefined,
        birthDate: formState.birthDate || null,
        genderCode: formState.genderCode || undefined,
        sportCode: formState.sportCode || initialSportCode || undefined,
        levelCode: formState.levelCode || undefined,
        relationshipCode: formState.relationshipCode || undefined,
      });
      setFormState({
        givenName: '',
        familyName: '',
        preferredName: '',
        birthDate: '',
        genderCode: '',
        sportCode: '',
        levelCode: '',
        relationshipCode: 'PARENT',
      });
      void refetchSchedule();
    } catch (createError: any) {
      setError(createError?.response?.data?.message || createError?.message || 'Could not add the athlete.');
    }
  };

  const handleBookingRequest = async (
    session: TenantFamilyScheduleSession,
    athleteBooking: TenantFamilyScheduleAthleteBooking,
    requestType: BookingAction,
    makeupCreditId?: number
  ) => {
    setBookingError(null);
    const requestKey = `${session.occurrenceId}-${athleteBooking.athleteId}-${requestType}-${makeupCreditId ?? 'standard'}`;
    setPendingRequestKey(requestKey);
    try {
      await requestBooking({
        occurrenceId: session.occurrenceId,
        payload: {
          athleteId: athleteBooking.athleteId,
          requestType,
          makeupCreditId,
          clientMutationId: `web-family-${session.occurrenceId}-${athleteBooking.athleteId}-${Date.now()}`,
        },
      });
    } catch (requestError: any) {
      setBookingError(requestError?.response?.data?.message || requestError?.message || 'Could not send this request.');
    } finally {
      setPendingRequestKey(null);
    }
  };

  const handlePayRegistration = async (receipt: TenantFamilyReceiptSummary) => {
    setPaymentError(null);
    try {
      const response = await registrationCheckout.mutateAsync({
        registrationId: receipt.registrationId,
        payload: buildCheckoutReturnUrls(),
      });
      if (response.checkoutUrl) {
        window.location.assign(response.checkoutUrl);
      }
    } catch (checkoutError: any) {
      console.error('Failed to create checkout session', checkoutError);
      setPaymentError(
        checkoutError?.response?.data?.message ||
          checkoutError?.message ||
          'Payment checkout is not available right now. Try again or contact the coach.'
      );
    }
  };

  if (!canUseFamilyFlow) {
    return (
      <Card variant="glass">
        <CardHeader>
          <CardTitle>Family</CardTitle>
          <CardDescription>Select an organization first, then come back here to manage athletes and schedule requests.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !household) {
    return (
      <Card variant="default" className="max-w-xl">
        <CardHeader>
          <CardTitle>Family unavailable</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-text-muted">
            The household view could not be loaded right now. Try again and we will restore your current organization context.
          </p>
          <Button onClick={() => refetch()}>Retry</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-6xl space-y-6 pb-24 md:pb-8">
      <header className="flex flex-col gap-4 border-b border-border pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-text-subtle">Family</p>
          <h1 className="mt-1.5 text-2xl font-semibold tracking-tight text-text sm:text-3xl">
            {tenantName}
          </h1>
          <p className="mt-1 text-sm text-text-muted">
            {household.guardian?.displayName || user?.displayName || 'Guardian'}
          </p>
        </div>

        <div className="grid gap-2 grid-cols-2 sm:grid-cols-4 lg:min-w-[520px]">
          <MiniMetric label="Athletes" value={household.athletes.length} />
          <MiniMetric label="Sessions" value={stats?.sessionCount ?? 0} />
          <MiniMetric label="Requests" value={(stats?.requestedCount ?? 0) + (stats?.trialRequestedCount ?? 0)} />
          <MiniMetric label="Makeups" value={makeupStats?.openCount ?? 0} />
        </div>
      </header>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <main className="min-w-0">
          <Card variant="default" padding="none">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
              <div>
                <CardTitle className="text-base">Schedule requests</CardTitle>
                <p className="mt-1 text-xs text-text-muted">Request a spot, join a waitlist, or ask for a trial.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <a
                  className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-border bg-transparent px-3 text-xs font-medium text-text transition hover:border-primary/50 hover:bg-surface-elevated hover:text-primary"
                  href="/api/v1/web/tenant-family/schedule/calendar.ics"
                >
                  <CalendarDays className="h-3.5 w-3.5" />
                  Calendar
                </a>
                <Button size="sm" variant="outline" onClick={() => refetchSchedule()}>
                  Refresh
                </Button>
              </div>
            </div>

            <CardContent className="space-y-4 p-5">
              {bookingError ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {bookingError}
                </div>
              ) : null}

              {isScheduleLoading ? (
                <div className="flex items-center gap-3 rounded-2xl border border-border bg-surface-elevated/20 p-4 text-sm text-text-muted">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  Loading schedule
                </div>
              ) : null}

              {isScheduleError ? (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                  <p className="text-sm font-semibold text-amber-800">Schedule unavailable</p>
                  <Button className="mt-3" size="sm" variant="outline" onClick={() => refetchSchedule()}>
                    Retry
                  </Button>
                </div>
              ) : null}

              {!isScheduleLoading && schedule && schedule.sessions.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border bg-surface-elevated/20 p-6 text-center">
                  <CalendarDays className="mx-auto h-7 w-7 text-text-subtle" />
                  <p className="mt-3 font-semibold text-text">{schedule.emptyStateTitle || 'No upcoming sessions'}</p>
                  <p className="mt-1 text-sm text-text-muted">
                    {schedule.emptyStateDescription || 'Sessions that match this household will appear here.'}
                  </p>
                </div>
              ) : null}

              {schedule?.sessions.map((session) => (
                <SessionRequestCard
                  key={session.occurrenceId}
                  session={session}
                  pendingRequestKey={pendingRequestKey}
                  openMakeupCreditsByAthleteId={openMakeupCreditsByAthleteId}
                  onRequest={handleBookingRequest}
                />
              ))}
            </CardContent>
          </Card>
        </main>

        <aside className="min-w-0 space-y-6">
          <MakeupsPanel
            makeupCredits={schedule?.makeupCredits ?? []}
            makeupSummary={schedule?.makeupSummary ?? null}
          />

          <FamilyBookableOpeningsPanel athleteId={selectedAthleteId} />

          <FamilyMatchPlayPanel athleteId={selectedAthleteId} />

          <FamilyProgressPanel development={developmentSummaries} />

          <FamilyBalancesPanel balances={balanceSummaries} />

          <FamilyReceiptsPanel
            receipts={receiptSummaries}
            payingRegistrationId={registrationCheckout.isPending ? registrationCheckout.variables?.registrationId ?? null : null}
            onPay={CARD_CHECKOUT_ENABLED ? (receipt) => void handlePayRegistration(receipt) : undefined}
          />
          {paymentError ? (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700">
              {paymentError}
            </p>
          ) : null}

          <Card variant="default">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users2 className="h-5 w-5 text-primary" />
                Athletes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {household.athletes.length > 0 ? (
                household.athletes.map((athlete) => (
                  <button
                    type="button"
                    key={athlete.athleteId}
                    onClick={() => setSelectedAthleteId(athlete.athleteId)}
                    className={`w-full rounded-2xl border p-4 text-left transition ${selectedAthleteId === athlete.athleteId ? 'border-primary/40 bg-primary/10' : 'border-border bg-surface-elevated/30 hover:border-primary/30'}`}
                  >
                    <p className="font-semibold text-text">
                      {athlete.preferredName || athlete.givenName} {athlete.familyName}
                    </p>
                    <p className="mt-1 text-xs uppercase tracking-[0.16em] text-text-subtle">
                      {[athlete.sportCode, athlete.levelCode, athlete.relationshipCode].filter(Boolean).join(' / ') || 'Athlete'}
                    </p>
                  </button>
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-border bg-surface-elevated/20 p-5">
                  <p className="font-semibold text-text">{household.emptyStateTitle || 'No athletes yet'}</p>
                  <p className="mt-2 text-sm text-text-muted">
                    {household.emptyStateDescription || 'Add your first athlete to unlock schedule requests.'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <FamilyNotesPanel
            athlete={selectedAthlete}
            sessions={schedule?.sessions ?? []}
            enabled={Boolean(user && canUseFamilyFlow && selectedAthlete)}
          />

          <Card variant="default">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HeartHandshake className="h-5 w-5 text-primary" />
                Add athlete
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={handleCreateAthlete}>
                <div className="grid gap-4">
                  <Input
                    label="Given name"
                    value={formState.givenName}
                    onChange={(event) => setFormState((current) => ({ ...current, givenName: event.target.value }))}
                  />
                  <Input
                    label="Family name"
                    value={formState.familyName}
                    onChange={(event) => setFormState((current) => ({ ...current, familyName: event.target.value }))}
                  />
                  <Input
                    label="Preferred name"
                    value={formState.preferredName}
                    onChange={(event) => setFormState((current) => ({ ...current, preferredName: event.target.value }))}
                    placeholder="Optional"
                  />
                  <Input
                    label="Birth date"
                    type="date"
                    value={formState.birthDate}
                    onChange={(event) => setFormState((current) => ({ ...current, birthDate: event.target.value }))}
                  />
                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1">
                    <Input
                      label="Sport"
                      value={formState.sportCode}
                      onChange={(event) => setFormState((current) => ({ ...current, sportCode: event.target.value }))}
                      placeholder={initialSportCode || 'Default'}
                    />
                    <Input
                      label="Level"
                      value={formState.levelCode}
                      onChange={(event) => setFormState((current) => ({ ...current, levelCode: event.target.value }))}
                      placeholder="Optional"
                    />
                  </div>
                </div>
                {error ? <p className="text-sm text-red-500">{error}</p> : null}
                <Button type="submit" isLoading={isCreatingAthlete} leftIcon={<Plus className="h-4 w-4" />} className="w-full">
                  Add athlete
                </Button>
              </form>
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function FamilyProgressPanel({ development }: { development: TenantFamilyAthleteDevelopmentSummary[] }) {
  if (development.length === 0) {
    return null;
  }

  return (
    <Card variant="default">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Flag className="h-5 w-5 text-primary" />
          Goals & practice
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {development.map((summary) => {
          const items = [...(summary.goals ?? []), ...(summary.assignments ?? [])].slice(0, 4);
          return (
            <div key={summary.athleteId} className="rounded-2xl border border-border bg-surface-elevated/30 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-text">{summary.athleteDisplayName || 'Athlete'}</p>
                  <p className="mt-1 text-xs text-text-muted">
                    {summary.activeGoalCount} active goals • {summary.openAssignmentCount} open practice
                  </p>
                </div>
                <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary">
                  Shared
                </span>
              </div>
              <div className="mt-3 space-y-2">
                {items.map((item) => (
                  <div key={`${item.itemId}-${item.title}`} className="border-t border-border pt-2">
                    <div className="flex items-start justify-between gap-3">
                      <p className="min-w-0 truncate text-sm font-semibold text-text">{item.title || 'Practice item'}</p>
                      <span className="shrink-0 text-xs font-semibold text-primary">{formatCodeLabel(item.status)}</span>
                    </div>
                    <p className="mt-1 text-xs text-text-muted">
                      {[item.detail, item.targetDate || item.dueAt ? `Due ${formatOptionalDate(item.targetDate || item.dueAt)}` : null]
                        .filter(Boolean)
                        .join(' • ') || 'Shared by coach'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function FamilyBalancesPanel({ balances }: { balances: TenantFamilyAthleteBalanceSummary[] }) {
  if (balances.length === 0) {
    return null;
  }

  return (
    <Card variant="default">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-primary" />
          Balances
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {balances.map((balance) => (
          <div key={balance.athleteId} className="rounded-2xl border border-border bg-surface-elevated/30 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-text">{balance.athleteDisplayName || 'Athlete'}</p>
                <p className="mt-1 text-xs text-text-muted">
                  {formatCredits(balance.creditsRemaining)} credits • {balance.unpaidBookingCount} unpaid
                </p>
              </div>
              <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary">
                {balance.activePackageCount} plans
              </span>
            </div>
            {(balance.packages ?? []).slice(0, 3).map((pkg) => (
              <div key={pkg.athletePackageId} className="mt-3 border-t border-border pt-2">
                <div className="flex items-start justify-between gap-3">
                  <p className="min-w-0 truncate text-sm font-semibold text-text">{pkg.packageLabel || 'Package'}</p>
                  <span className="shrink-0 text-xs font-semibold text-primary">{formatCodeLabel(pkg.paymentStatus)}</span>
                </div>
                <p className="mt-1 text-xs text-text-muted">
                  {formatCredits(pkg.creditsRemaining)} left{pkg.expiresAt ? ` • expires ${formatOptionalDate(pkg.expiresAt)}` : ''}
                </p>
              </div>
            ))}
            {balance.securityNotice ? (
              <p className="mt-3 text-xs leading-5 text-text-subtle">{balance.securityNotice}</p>
            ) : null}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function FamilyReceiptsPanel({
  receipts,
  payingRegistrationId,
  onPay,
}: {
  receipts: TenantFamilyReceiptSummary[];
  payingRegistrationId?: number | null;
  onPay?: (receipt: TenantFamilyReceiptSummary) => void;
}) {
  if (receipts.length === 0) {
    return null;
  }

  return (
    <Card variant="default">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ReceiptText className="h-5 w-5 text-primary" />
          Receipts
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {receipts.map((receipt) => {
          const receiptUrl = getReceiptUrl(receipt);
          const paymentUrl = receipt.canPayOnline ? receipt.externalPaymentUrl : null;
          const canStartCheckout = CARD_CHECKOUT_ENABLED && receipt.canPayOnline && !paymentUrl && Boolean(onPay);
          const isPaying = payingRegistrationId === receipt.registrationId;
          const needsOutsidePayment = receipt.paymentStatus !== 'PAID'
            && (receipt.canPayOnline || Number(receipt.balanceAmount ?? 0) > 0);
          return (
            <div key={receipt.registrationId} className="rounded-2xl border border-border bg-surface-elevated/30 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-text">{receipt.programName || 'Program registration'}</p>
                  <p className="mt-1 text-xs text-text-muted">
                    {receipt.athleteDisplayName || 'Athlete'} • {formatCodeLabel(receipt.paymentStatus || receipt.registrationStatus)}
                  </p>
                </div>
                <span className="shrink-0 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary">
                  {formatMoney(receipt.balanceAmount ?? receipt.priceAmount, receipt.currencyCode)}
                </span>
              </div>
              <div className="mt-3 border-t border-border pt-2 text-xs text-text-muted">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>{receipt.receiptNumber || receipt.invoiceNumber || `Registration #${receipt.registrationId}`}</span>
                  <span>{formatOptionalDate(receipt.paidAt || receipt.createdAt)}</span>
                </div>
                {receipt.paymentMethodLabel ? (
                  <p className="mt-2 leading-5 text-text-subtle">{receipt.paymentMethodLabel}</p>
                ) : null}
                {needsOutsidePayment && !CARD_CHECKOUT_ENABLED && !paymentUrl ? (
                  <p className="mt-2 leading-5 text-text-subtle">{PAY_OUTSIDE_COPY}</p>
                ) : null}
              </div>
              {paymentUrl || canStartCheckout || receiptUrl ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {paymentUrl ? (
                    <a
                      href={paymentUrl}
                      target="_blank"
                      rel="noreferrer"
                      className={cn(
                        'inline-flex h-8 items-center justify-center gap-1.5 rounded-lg bg-primary px-3 text-xs font-medium [color:var(--color-on-primary)]',
                        'transition-all duration-200 ease-out hover:bg-[color:var(--color-primary-hover)]'
                      )}
                    >
                      Pay
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  ) : null}
                  {canStartCheckout ? (
                    <button
                      type="button"
                      disabled={isPaying}
                      onClick={() => onPay?.(receipt)}
                      className={cn(
                        'inline-flex h-8 items-center justify-center gap-1.5 rounded-lg bg-primary px-3 text-xs font-medium [color:var(--color-on-primary)]',
                        'transition-all duration-200 ease-out hover:bg-[color:var(--color-primary-hover)] disabled:cursor-not-allowed disabled:opacity-60'
                      )}
                    >
                      {isPaying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                      Pay
                      {!isPaying ? <ExternalLink className="h-3.5 w-3.5" /> : null}
                    </button>
                  ) : null}
                  {receiptUrl ? (
                    <a
                      href={receiptUrl}
                      target="_blank"
                      rel="noreferrer"
                      className={cn(
                        'inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-border bg-transparent px-3 text-xs font-medium text-text',
                        'transition-all duration-200 ease-out hover:border-primary/50 hover:bg-surface-elevated hover:text-primary'
                      )}
                    >
                      Receipt
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function SessionRequestCard({
  session,
  pendingRequestKey,
  openMakeupCreditsByAthleteId,
  onRequest,
}: {
  session: TenantFamilyScheduleSession;
  pendingRequestKey: string | null;
  openMakeupCreditsByAthleteId: Map<number, TenantFamilyMakeupCreditSummary[]>;
  onRequest: (
    session: TenantFamilyScheduleSession,
    athleteBooking: TenantFamilyScheduleAthleteBooking,
    requestType: BookingAction,
    makeupCreditId?: number
  ) => void;
}) {
  const isFull = session.openSpots != null && session.openSpots <= 0;
  const capacityLabel =
    session.capacity == null
      ? `${session.bookedCount} booked`
      : `${session.bookedCount}/${session.capacity} booked`;

  return (
    <article className="rounded-[24px] border border-border bg-surface-elevated/20 p-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
              {formatSessionDate(session.startsAt)}
            </span>
            {session.programType ? (
              <span className="rounded-full border border-border bg-surface px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-text-subtle">
                {formatCodeLabel(session.programType)}
              </span>
            ) : null}
            {isFull ? (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-700">
                Waitlist
              </span>
            ) : null}
          </div>

          <h2 className="mt-3 text-lg font-semibold text-text">{session.title}</h2>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-text-muted">
            <span className="inline-flex items-center gap-1.5">
              <Clock3 className="h-4 w-4" />
              {formatTimeRange(session.startsAt, session.endsAt)}
            </span>
            {session.locationLabel ? (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-4 w-4" />
                {session.locationLabel}
              </span>
            ) : null}
            <span>{capacityLabel}</span>
            {session.waitlistedCount > 0 ? <span>{session.waitlistedCount} waitlisted</span> : null}
          </div>
        </div>

        {session.requestDisabledReason ? (
          <span className="rounded-full border border-border bg-surface px-3 py-1 text-xs font-semibold text-text-muted">
            {session.requestDisabledReason}
          </span>
        ) : null}
      </div>

      <div className="mt-4 space-y-2">
        {session.athleteBookings.map((athleteBooking) => {
          const primaryAction: BookingAction = isFull ? 'WAITLIST' : 'REQUEST';
          const primaryLabel = isFull ? 'Join waitlist' : 'Request';
          const primaryKey = `${session.occurrenceId}-${athleteBooking.athleteId}-${primaryAction}`;
          const trialKey = `${session.occurrenceId}-${athleteBooking.athleteId}-TRIAL`;
          const availableMakeupCredits = openMakeupCreditsByAthleteId.get(athleteBooking.athleteId) ?? [];
          const makeupCredit = session.makeUpEligible && athleteBooking.canRequestMakeup ? availableMakeupCredits[0] : null;
          const makeupKey = `${session.occurrenceId}-${athleteBooking.athleteId}-${primaryAction}-${makeupCredit?.makeupCreditId ?? 'standard'}`;

          return (
            <div
              key={athleteBooking.athleteId}
              className="flex flex-col gap-3 rounded-2xl border border-border bg-white/80 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <p className="font-semibold text-text">{athleteBooking.athleteDisplayName}</p>
                <BookingStatusChip booking={athleteBooking} />
              </div>

              {athleteBooking.canRequest ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => onRequest(session, athleteBooking, primaryAction)}
                    isLoading={pendingRequestKey === `${primaryKey}-standard`}
                  >
                    {primaryLabel}
                  </Button>
                  {makeupCredit ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onRequest(session, athleteBooking, primaryAction, makeupCredit.makeupCreditId)}
                      isLoading={pendingRequestKey === makeupKey}
                    >
                      Use makeup
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onRequest(session, athleteBooking, 'TRIAL')}
                    isLoading={pendingRequestKey === `${trialKey}-standard`}
                  >
                    Trial
                  </Button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </article>
  );
}

function MakeupsPanel({
  makeupCredits,
  makeupSummary,
}: {
  makeupCredits: TenantFamilyMakeupCreditSummary[];
  makeupSummary: { openCount: number; scheduledCount: number; totalCount: number } | null;
}) {
  const visibleCredits = makeupCredits.slice(0, 5);

  return (
    <Card variant="default">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCcw className="h-5 w-5 text-primary" />
          Makeups
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <MiniMetric label="Open" value={makeupSummary?.openCount ?? 0} />
          <MiniMetric label="Set" value={makeupSummary?.scheduledCount ?? 0} />
          <MiniMetric label="Total" value={makeupSummary?.totalCount ?? 0} />
        </div>

        {visibleCredits.length > 0 ? (
          <div className="space-y-2">
            {visibleCredits.map((credit) => (
              <div key={credit.makeupCreditId} className="rounded-2xl border border-border bg-surface-elevated/30 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-text">{credit.athleteDisplayName}</p>
                    <p className="mt-1 text-xs text-text-muted">
                      {credit.scheduledTitle
                        ? `Scheduled: ${credit.scheduledTitle}`
                        : credit.sourceTitle
                          ? `From: ${credit.sourceTitle}`
                          : 'Makeup credit'}
                    </p>
                  </div>
                  <span className={cn('shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]', makeupStatusTone(credit.creditStatus))}>
                    {formatCodeLabel(credit.creditStatus)}
                  </span>
                </div>
                <p className="mt-2 text-[11px] uppercase tracking-[0.16em] text-text-subtle">
                  {credit.scheduledStartsAt
                    ? formatSessionDate(credit.scheduledStartsAt)
                    : credit.sourceStartsAt
                      ? formatSessionDate(credit.sourceStartsAt)
                      : formatCodeLabel(credit.reasonCode)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-border bg-surface-elevated/20 p-4">
            <p className="text-sm font-semibold text-text">No makeups open</p>
            <p className="mt-1 text-xs text-text-muted">Coach-approved makeup credits will show here.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BookingStatusChip({ booking }: { booking: TenantFamilyScheduleAthleteBooking }) {
  const status = (booking.bookingStatus || '').toUpperCase();
  if (!status) {
    return <p className="mt-1 text-xs text-text-subtle">{booking.actionLabel || 'Not requested'}</p>;
  }

  const tone =
    status === 'BOOKED'
      ? 'border-primary/20 bg-primary/10 text-primary'
      : status === 'WAITLISTED'
        ? 'border-amber-200 bg-amber-50 text-amber-700'
        : status === 'CANCELLED'
          ? 'border-zinc-200 bg-zinc-50 text-zinc-600'
          : 'border-primary/20 bg-primary/10 text-primary';

  return (
    <span className={cn('mt-1 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em]', tone)}>
      {status === 'BOOKED' ? <CheckCircle2 className="h-3 w-3" /> : null}
      {booking.actionLabel || formatCodeLabel(status)}
    </span>
  );
}

function makeupStatusTone(status?: string | null) {
  switch ((status || '').toUpperCase()) {
    case 'OPEN':
      return 'border-primary/20 bg-primary/10 text-primary';
    case 'SCHEDULED':
      return 'border-sky-200 bg-sky-50 text-sky-700';
    case 'USED':
      return 'border-primary/20 bg-primary/10 text-primary';
    case 'WAIVED':
      return 'border-zinc-200 bg-zinc-50 text-zinc-600';
    default:
      return 'border-border bg-surface text-text-muted';
  }
}

function MiniMetric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-border bg-surface-elevated/20 px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">{label}</p>
      <p className="mt-1.5 text-2xl font-semibold tabular-nums tracking-tight text-text">{value}</p>
    </div>
  );
}

function getReceiptUrl(receipt: TenantFamilyReceiptSummary) {
  if (receipt.receiptUrl) {
    return receipt.receiptUrl;
  }
  if (receipt.receiptShareEnabled && receipt.receiptShareToken) {
    return `/api/v1/public/program-registration-documents/receipts/${encodeURIComponent(receipt.receiptShareToken)}`;
  }
  return null;
}

function buildCheckoutReturnUrls() {
  const fallbackUrl = 'https://playtark.com/family';
  const currentUrl = typeof window === 'undefined' ? fallbackUrl : window.location.href;
  return {
    successUrl: currentUrl,
    cancelUrl: currentUrl,
  };
}

function formatMoney(value?: number | null, currency?: string | null) {
  const amount = Number(value ?? 0);
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

function formatCredits(value?: number | null) {
  const amount = Number(value ?? 0);
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(1);
}

function formatOptionalDate(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatSessionDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Session';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTimeRange(startsAt: string, endsAt: string) {
  return `${formatTime(startsAt)} - ${formatTime(endsAt)}`;
}

function formatTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(' ', '').toLowerCase();
}

function formatCodeLabel(value?: string | null) {
  if (!value) return '';
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
